/**
 * Delivery staff API — delivery_staff_token for /api/delivery-staff/*.
 * Owner CRUD uses the existing LP vendor axios client (lp_vendor_token).
 */
import axios from 'axios';
import api from '../api';
import { TOKEN_KEY } from '../constants';

let API_URL = 'https://api.gosnel.com';
const ENV_API = import.meta.env.VITE_API_BASE_URL;
if (ENV_API && ENV_API !== 'undefined' && !String(ENV_API).includes('192.168')) {
  API_URL = ENV_API;
  if (API_URL.startsWith('http://') && !API_URL.includes('localhost')) {
    API_URL = API_URL.replace('http://', 'https://');
  }
}

if (typeof window !== 'undefined') {
  const host = (window.location.hostname || '').toLowerCase();
  const onGosnel =
    host === 'gosnel.com' ||
    host.endsWith('.gosnel.com') ||
    host.endsWith('.pages.dev');
  if (onGosnel && (API_URL.includes('localhost') || API_URL.includes('127.0.0.1'))) {
    API_URL = 'https://api.gosnel.com';
  }
}

const client = axios.create({
  baseURL: API_URL,
  timeout: 45000,
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
  },
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('delivery_staff_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const STAFF_PREFIX = '/api/delivery-staff';
const OWNER_PREFIX = '/api/vendor/delivery-staff';

/** Standalone local.gosnel.com app — no /vendor prefix. */
export function staffAppPath(path) {
  return path.startsWith('/') ? path : `/${path}`;
}

export function getStaffSession() {
  try {
    return {
      token: localStorage.getItem('delivery_staff_token'),
      staffId: localStorage.getItem('delivery_staff_id'),
      vendorId: localStorage.getItem('delivery_staff_vendor_id'),
      displayName: localStorage.getItem('delivery_staff_display_name'),
    };
  } catch {
    return {
      token: null,
      staffId: null,
      vendorId: null,
      displayName: null,
    };
  }
}

export function clearStaffSession() {
  try {
    localStorage.removeItem('delivery_staff_token');
    localStorage.removeItem('delivery_staff_id');
    localStorage.removeItem('delivery_staff_vendor_id');
    localStorage.removeItem('delivery_staff_display_name');
  } catch {
    /* ignore */
  }
}

export function saveStaffSession(data) {
  localStorage.setItem('delivery_staff_token', data.access_token);
  localStorage.setItem('delivery_staff_id', data.staff_id || '');
  localStorage.setItem('delivery_staff_vendor_id', data.vendor_id || '');
  localStorage.setItem(
    'delivery_staff_display_name',
    data.display_name || 'Staff'
  );
  localStorage.setItem('delivery_staff_last_role', 'staff');
}

export function hasVendorSession() {
  try {
    return !!(localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY));
  } catch {
    return false;
  }
}

export const deliveryStaffApi = {
  login: async ({ username, password, vendorId } = {}) => {
    const body = { username, password };
    if (vendorId) body.vendor_id = vendorId;
    const { data } = await client.post(`${STAFF_PREFIX}/login`, body);
    saveStaffSession(data);
    return data;
  },
  me: async () => {
    const { data } = await client.get(`${STAFF_PREFIX}/me`);
    return data;
  },
  listOrders: async ({ q, building } = {}) => {
    const params = {};
    if (q) params.q = q;
    if (building) params.building = building;
    const { data } = await client.get(`${STAFF_PREFIX}/orders`, { params });
    return data?.orders || [];
  },
  takeOrder: async (orderId, orderKind) => {
    const params = {};
    if (orderKind) params.order_kind = orderKind;
    const { data } = await client.post(
      `${STAFF_PREFIX}/orders/${orderId}/take`,
      null,
      { params }
    );
    return data;
  },
  onTheWay: async (orderId, orderKind) => {
    const params = {};
    if (orderKind) params.order_kind = orderKind;
    const { data } = await client.post(
      `${STAFF_PREFIX}/orders/${orderId}/on-the-way`,
      null,
      { params }
    );
    return data;
  },
  releaseOrder: async (orderId, orderKind) => {
    const params = {};
    if (orderKind) params.order_kind = orderKind;
    const { data } = await client.post(
      `${STAFF_PREFIX}/orders/${orderId}/release`,
      null,
      { params }
    );
    return data;
  },
  getPodChallenge: async (orderId, orderKind) => {
    const params = {};
    if (orderKind) params.order_kind = orderKind;
    const { data } = await client.get(
      `${STAFF_PREFIX}/orders/${orderId}/pod-challenge`,
      { params }
    );
    return data;
  },
  confirmDelivery: async (
    orderId,
    { code, exceptionReason, note, orderKind } = {}
  ) => {
    const body = {};
    if (code != null) body.code = code;
    if (exceptionReason) body.exception_reason = exceptionReason;
    if (note) body.note = note;
    if (orderKind) body.order_kind = orderKind;
    const { data } = await client.post(
      `${STAFF_PREFIX}/orders/${orderId}/confirm-delivery`,
      body
    );
    return data;
  },
  getVapidPublicKey: async () => {
    const { data } = await client.get(`${STAFF_PREFIX}/push/vapid-public-key`);
    return data;
  },
  subscribePush: async (payload) => {
    const { data } = await client.post(`${STAFF_PREFIX}/push/subscribe`, payload);
    return data;
  },
  unsubscribePush: async (endpoint) => {
    const { data } = await client.post(`${STAFF_PREFIX}/push/unsubscribe`, {
      endpoint,
    });
    return data;
  },
};

/** Owner (LP vendor JWT) CRUD helpers via default api export. */
export const deliveryStaffOwnerApi = {
  listUsers: async () => {
    const { data } = await api.get(`${OWNER_PREFIX}/users`);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.users)) return data.users;
    return [];
  },
  createUser: async (payload) => {
    const { data } = await api.post(`${OWNER_PREFIX}/users`, payload);
    return data;
  },
  updateUser: async (id, payload) => {
    const { data } = await api.patch(`${OWNER_PREFIX}/users/${id}`, payload);
    return data;
  },
  resetPassword: async (id, newPassword) => {
    const { data } = await api.post(`${OWNER_PREFIX}/users/${id}/reset-password`, {
      new_password: newPassword,
    });
    return data;
  },
  deactivateUser: async (id) => {
    const { data } = await api.delete(`${OWNER_PREFIX}/users/${id}`);
    return data;
  },
  takeOrder: async (orderId, { orderKind } = {}) => {
    const params = {};
    if (orderKind) params.order_kind = orderKind;
    const { data } = await api.post(
      `${OWNER_PREFIX}/orders/${orderId}/take`,
      null,
      { params }
    );
    return data;
  },
  onTheWay: async (orderId, { orderKind } = {}) => {
    const params = {};
    if (orderKind) params.order_kind = orderKind;
    const { data } = await api.post(
      `${OWNER_PREFIX}/orders/${orderId}/on-the-way`,
      null,
      { params }
    );
    return data;
  },
  releaseOrder: async (orderId, { orderKind } = {}) => {
    const params = {};
    if (orderKind) params.order_kind = orderKind;
    const { data } = await api.post(
      `${OWNER_PREFIX}/orders/${orderId}/release`,
      null,
      { params }
    );
    return data;
  },
  getPodChallenge: async (orderId, { orderKind, revealReal } = {}) => {
    const params = {};
    if (orderKind) params.order_kind = orderKind;
    if (revealReal) params.reveal_real = true;
    const { data } = await api.get(
      `${OWNER_PREFIX}/orders/${orderId}/pod-challenge`,
      { params }
    );
    return data;
  },
  confirmDelivery: async (
    orderId,
    { code, exceptionReason, note, orderKind } = {}
  ) => {
    const body = {};
    if (code != null) body.code = code;
    if (exceptionReason) body.exception_reason = exceptionReason;
    if (note) body.note = note;
    if (orderKind) body.order_kind = orderKind;
    const { data } = await api.post(
      `${OWNER_PREFIX}/orders/${orderId}/confirm-delivery`,
      body
    );
    return data;
  },
};

export default deliveryStaffApi;
